
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    permit_type: string;
    permit_authority: string;
    permit_number: string;
    issue_date: string;
    expiry_date: string;
    amount: string;
    document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddPermitRenewal() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [permitTypes, setPermitTypes] = useState<any[]>([]);
    const [permitAuthorities, setPermitAuthorities] = useState<any[]>([]);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");
    const { config } = useConfig();

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedIssueDate = watch("issue_date");
    const watchedExpiryDate = watch("expiry_date");

    // Pending check
    useEffect(() => {
        if (!selectedVehicle) { setSubmissionBlocked(false); setPendingMessage(""); return; }
        const checkPending = async () => {
            try {
                const response: any = await apiPost("permit_renewal/check_pending", { vehicle_id: selectedVehicle });
                if (response?.exists) { setPendingMessage(response.message); setSubmissionBlocked(true); }
                else { setSubmissionBlocked(false); setPendingMessage(""); }
            } catch { setSubmissionBlocked(false); setPendingMessage(""); }
        };
        checkPending();
    }, [selectedVehicle]);

    // Fetch Branches & Permit Types on Mount
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const branchData: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchData);

                const permitData: any = await apiPost('master-entries/list', { type: 'permit_type' });
                setPermitTypes(Array.isArray(permitData) ? permitData : []);

                const authorityData: any = await apiPost('master-entries/list', { type: 'permit_authority' });
                setPermitAuthorities(Array.isArray(authorityData) ? authorityData : []);
            } catch (error) {
                console.error(error);
            }
        };
        fetchInitialData();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };

        fetchVehicles();
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "A pending permit renewal request already exists for this vehicle.");
            return;
        }

        setLoading(true);
        try {
            let docName = "";
            if (data.document && data.document.length > 0) {
                const file = data.document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                docName = await uploadFile(file);
            }

            const payload = {
                ...data,
                document: docName,
                // Auto-determine status: Completed if all mandatory fields are present
                status: (
                    data.branch_id &&
                    data.vehicle_id &&
                    data.permit_type &&
                    data.issue_date &&
                    data.expiry_date &&
                    data.amount &&
                    docName
                ) ? 'Completed' : 'Pending',
            };

            await apiPost("permit_renewal/store", payload);
            toast.success("Permit Renewal added successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/permit_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding Permit renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Permit Renewal" parent="Renewal" title="Add Permit Renewal" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Renewal Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Permit Type <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="permit_type"
                                        control={control}
                                        rules={{ required: "Permit Type is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.permit_type}>
                                                    <option value="">Select Type</option>
                                                    {permitTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.permit_type?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Permit Authority</Label>
                                    <Controller
                                        name="permit_authority"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.permit_authority}>
                                                    <option value="">Select Authority</option>
                                                    {permitAuthorities.map((a: any) => <option key={a.id} value={a.name}>{a.name}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.permit_authority?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Permit Number</Label>
                                    <Controller
                                        name="permit_number"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Permit No" invalid={!!errors.permit_number} autoComplete="off" />
                                                <FormFeedback>{errors.permit_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Issue Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="issue_date"
                                        control={control}
                                        rules={{ required: "Issue Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.issue_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedExpiryDate && d && new Date(d) > new Date(watchedExpiryDate)) {
                                                            toast.error("Issue date cannot be after Expiry date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.issue_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Expiry Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="expiry_date"
                                        control={control}
                                        rules={{ required: "Expiry Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedIssueDate && d && new Date(d) < new Date(watchedIssueDate)) {
                                                            toast.error("Expiry date cannot be before Issue date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Renewal Amount ({config.currency}) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="amount"
                                        control={control}
                                        rules={{ required: "Amount is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder={`Amount (${config.currency})`} invalid={!!errors.amount} autoComplete="off" />
                                                <FormFeedback>{errors.amount?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Permit Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="document"
                                        control={control}
                                        rules={{ required: "Document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = "";
                                                                onChange(null);
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    invalid={!!errors.document}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Remarks</Label>
                                    <Controller
                                        name="remarks"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Any remarks..." />
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            {/* Pending warning */}
                            {submissionBlocked && (
                                <div className="alert alert-warning mt-3 py-2 text-danger">
                                    <strong>⚠ Blocked:</strong> {pendingMessage}
                                </div>
                            )}

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
